import React, { useState, useEffect } from 'react';
import { Input, Button, Space, Card, Table, Empty, DatePicker } from 'antd';
import { SearchOutlined, ClearOutlined, PrinterOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import PermissionGuard from '../../components/PermissionGuard';
import { Modules } from '../../constants/permissions';
import { usePW9322Query } from './hooks/usePW9322Query';
import type { LeaveRow } from '../../services/pw9322Api';
import { pw9322Api } from '../../services/pw9322Api';
import './PW9322Form.css';

const { RangePicker } = DatePicker;

const PW9322Form: React.FC = () => {
  const { loading, data, queried, noData, totalDay, runQuery, reset } = usePW9322Query();
  const [docFrom, setDocFrom] = useState('');
  const [docTo, setDocTo] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [deptCode, setDeptCode] = useState('');
  const [deptName, setDeptName] = useState('');

  useEffect(() => {
    // BR-I02: auto-populate dept from current user
    const userStr = localStorage.getItem('user') || sessionStorage.getItem('user');
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        const userId = user.userId || user.UserId || user.id || '';
        if (userId) {
          pw9322Api.getUserDept(String(userId)).then(info => {
            setDeptCode(info.dept_code || '');
            setDeptName(info.dept_name || '');
          }).catch(() => {});
        }
      } catch { /* ignore */ }
    }
  }, []);

  const handleQuery = () => {
    runQuery({
      docFrom: docFrom.trim() || undefined,
      docTo: docTo.trim() || undefined,
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
      deptCode: deptCode.trim() || undefined,
    });
  };

  const handleReset = () => {
    setDocFrom('');
    setDocTo('');
    setDateFrom('');
    setDateTo('');
    reset();
  };

  const handlePrint = () => {
    if (!data || data.length === 0) return;
    const win = window.open('', '_blank');
    if (!win) return;
    win.document.write(`<!DOCTYPE html><html><head><title>請假明細表（部門用）</title>
<style>body{font-family:'Microsoft JhengHei',sans-serif;margin:20px;font-size:12px}h2{text-align:center}table{width:100%;border-collapse:collapse}th,td{border:1px solid #333;padding:3px 6px}th{background:#f0f0f0;text-align:center}.right{text-align:right}@media print{body{margin:10mm}}</style></head><body>
<h2>請假明細表（部門用）</h2>
<table><thead><tr><th>假單號</th><th>員工編號</th><th>姓名</th><th>部門</th><th>假別</th><th>請假日期</th><th>開始時間</th><th>結束日期</th><th>結束時間</th><th>單位</th><th>時數</th><th>事由</th></tr></thead><tbody>
${data.map(r => `<tr><td>${r.doc_no}</td><td>${r.emp_no}</td><td>${r.emp_name}</td><td>${r.dept_name}</td><td>${r.leave_type}</td><td>${r.leave_date?.substring(0,10)||''}</td><td>${r.start_time||''}</td><td>${r.end_date?.substring(0,10)||''}</td><td>${r.end_time||''}</td><td>${r.unit}</td><td class="right">${r.duration}</td><td>${r.reason}</td></tr>`).join('')}
</tbody></table>
<p style="margin-top:8px">合計請假天數：<strong>${totalDay}</strong> 天，共 ${data.length} 筆</p>
</body></html>`);
    win.document.close();
    win.focus();
    win.print();
  };

  const columns: ColumnsType<LeaveRow> = [
    { title: '假單號', dataIndex: 'doc_no', key: 'doc_no', width: 120 },
    { title: '員工編號', dataIndex: 'emp_no', key: 'emp_no', width: 90 },
    { title: '姓名', dataIndex: 'emp_name', key: 'emp_name', width: 90 },
    { title: '部門', dataIndex: 'dept_name', key: 'dept_name', width: 100 },
    { title: '假別', dataIndex: 'leave_type', key: 'leave_type', width: 80 },
    { title: '請假日期', dataIndex: 'leave_date', key: 'leave_date', width: 110, render: (v: string) => v?.substring(0,10)||'' },
    { title: '開始時間', dataIndex: 'start_time', key: 'start_time', width: 80 },
    { title: '結束日期', dataIndex: 'end_date', key: 'end_date', width: 110, render: (v: string) => v?.substring(0,10)||'' },
    { title: '結束時間', dataIndex: 'end_time', key: 'end_time', width: 80 },
    { title: '單位', dataIndex: 'unit', key: 'unit', width: 60 },
    { title: '時數', dataIndex: 'duration', key: 'duration', width: 70, align: 'right' as const },
    { title: '事由', dataIndex: 'reason', key: 'reason', ellipsis: true },
  ];

  return (
    <PermissionGuard module={Modules.PW9322} action="query">
      <div className="pw9322-container">
        <div className="pw9322-header">
          <h2>請假明細表（部門用）</h2>
          {queried && !noData && (
            <Button icon={<PrinterOutlined />} onClick={handlePrint}>列印</Button>
          )}
        </div>
        <Card size="small" style={{ marginBottom: 16 }}>
          <Space wrap size="middle">
            <div>
              <label style={{ display: 'block', fontSize: 12, color: '#666', marginBottom: 4 }}>假單號起</label>
              <Input value={docFrom} onChange={e => setDocFrom(e.target.value)} placeholder="起始單號" style={{ width: 130 }} allowClear />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 12, color: '#666', marginBottom: 4 }}>假單號迄</label>
              <Input value={docTo} onChange={e => setDocTo(e.target.value)} placeholder="結束單號" style={{ width: 130 }} allowClear />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 12, color: '#666', marginBottom: 4 }}>請假日期範圍</label>
              <RangePicker
                style={{ width: 240 }}
                onChange={(_, strs) => { setDateFrom(strs[0] || ''); setDateTo(strs[1] || ''); }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 12, color: '#666', marginBottom: 4 }}>部門</label>
              <Input value={deptCode} onChange={e => setDeptCode(e.target.value)} placeholder="部門代號" style={{ width: 110 }} allowClear addonAfter={deptName || undefined} />
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-end', paddingTop: 18 }}>
              <Space>
                <Button type="primary" icon={<SearchOutlined />} onClick={handleQuery} loading={loading}>查詢</Button>
                <Button icon={<ClearOutlined />} onClick={handleReset}>清除</Button>
              </Space>
            </div>
          </Space>
        </Card>
        {queried && noData && <Empty description="查無此資料" style={{ marginTop: 40 }} />}
        {queried && !noData && (
          <>
            <div style={{ marginBottom: 8, fontSize: 13, color: '#555' }}>
              合計請假天數：<strong>{totalDay}</strong> 天，共 {data.length} 筆
            </div>
            <Table<LeaveRow>
              columns={columns}
              dataSource={data}
              rowKey="doc_no"
              loading={loading}
              size="small"
              pagination={{ pageSize: 50, showSizeChanger: true, showTotal: t => `共 ${t} 筆` }}
              scroll={{ x: 1000 }}
            />
          </>
        )}
      </div>
    </PermissionGuard>
  );
};

export default PW9322Form;
